<template>
  <parent v-if="!distributionLoaded">
    <div class="dashboardWrapper">
      <div>
        <header-dashboard
          :items="distributions"
          :page-size="pageSize"
          :total-count="totalCount"
          multi-select-title="Distributions"
          :is-for-distribution="true"
          :is-any-request-loading="isAnyRequestLoading"
          @applyFilters="applyFilters"
          @fetchScrolledItems="fetchDistrubutionsForScroll"
        />
        <div class="d-flex align-items-center flex-wrap gap-16">
          <header-item
            v-for="item in headerItems"
            :key="item.id"
            class="mt-1"
            :total-title="item.title"
            :icon-name="item.iconName"
            :description="item.description"
            :value="item.value"
            :loading="trafficSumLoaded && hitMissesSumLoaded && hitMissesLoaded"
          />
        </div>
        <div>
          <pop-up
            v-if="distributionsEmpty"
            class="position-absolute custom-position"
            title="Create a Distribution"
            button-text="Go to Distributions"
            :show-cancel="false"
            :go-back="false"
            @add="$router.push({ name: 'distributions' })"
          >
            Before viewing analytics, kindly generate a distribution.
          </pop-up>
          <div>
            <b-tabs class="mt-3">
              <b-tab
                v-for="tab in tabs"
                :key="tab.id"
                :title="tab.title"
                :disabled="tab.disabled"
                :active="currentRouteName === tab.routeName"
                @click="changeView(tab.routeName)"
              />
            </b-tabs>
            <transition
              name="fade"
              mode="out-in"
            >
              <router-view
                :key="$route.path"
                class="mt-1"
              />
            </transition>
          </div>
        </div>
      </div>
    </div>
  </parent>
</template>

<script>

import HeaderDashboard from '@/components/analytics/header/HeaderDashboard.vue'
import HeaderItem from '@/components/analytics/header/HeaderItem.vue'
import PopUp from '@/components/popup/Pop-up.vue'

export default {
  name: 'CdnAnalytics',
  components: {
    HeaderDashboard,
    HeaderItem,
    PopUp,

  },
  data() {
    return {
      defaultDistributionId: [],
      selectedIds: [],
      filterVal: '',
      currentPage: 0,
      // default 24-h in seconds
      timeInterval: 86400,
      // default static aggreagation for 24-h
      staticAggregation: '30-m',
      currentTimestamp: Math.floor(Date.now() / 1000),
    }
  },
  computed: {
    currentRouteName() {
      return this.$route.name
    },
    tabs() {
      return [
        {
          id: 1,
          title: 'Bandwidth',
          routeName: 'analytics',
          disabled: this.distributionsEmpty,
          iconName: 'Checkmark',
        },
        {
          id: 2,
          title: 'Traffic',
          routeName: 'traffic',
          disabled: this.distributionsEmpty,
          iconName: '',
        },
        {
          id: 3,
          title: 'Requests',
          routeName: 'requests',
          disabled: this.distributionsEmpty,
          iconName: '',
        },
        {
          id: 4,
          title: 'Status Codes',
          routeName: 'status-codes',
          disabled: this.distributionsEmpty,
          iconName: '',
        },
      ]
    },
    distributionsEmpty() {
      return this.distributions.length === 0
    },
    pageSize() {
      return this.resultInfo.pageSize || 10
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    resultInfo() {
      return this.$store.state.cdn.scrolledDistributionsResultInfo
    },
    distributions() {
      return this.$store.state.cdn.distributions ?? []
    },
    distributionLoaded() {
      return this.$store.state.cdn.fetchingDistributions
    },
    trafficSumLoaded() {
      return this.$store.state.analytics.loadingTrafficSum
    },
    hitMissesSumLoaded() {
      return this.$store.state.analytics.loadingHitMissesSum
    },
    trafficSum() {
      return this.$store.state.analytics.trafficSum
    },
    totalHitRatioPercentage() {
      return this.$store.state.analytics.totalHitRatioPercentage
    },
    hitMissesLoaded() {
      return this.$store.state.analytics.loadingHitMisses
    },
    hitMissesSum() {
      return this.$store.state.analytics.hitMissesSum
    },
    headerItems() {
      const headerDetails = [
        {
          id: 1,
          iconName: 'Checkmark',
          title: 'Total traffic',
          description: `In the last ${this.filterVal || '24-h'}, your traffic was within the usual range.`,
          value: this.formatBytes(this.trafficSum.sum) || '-',
        },
        {
          id: 2,
          iconName: 'Checkmark',
          title: 'Total requests',
          description: `In the last ${this.filterVal || '24-h'}, your total requests reached 20M.`,
          value: this.formatNumberWithUnit(this.hitMissesSum.sum),
        },
        {
          id: 3,
          iconName: 'Checkmark',
          title: 'Hit ratio',
          description: `In the last ${this.filterVal || '24-h'}, your hit ratio was higher than the standard average of 80%.`,
          value: this.totalHitRatioPercentage,
        },
      ]
      return headerDetails
    },
    isAnyRequestLoading() {
      return this.distributionsEmpty
        || this.hitMissesLoaded
        || this.hitMissesDetailsLoaded
        || this.hitMissesSumLoaded
        || this.trafficSumLoaded
        || this.trafficLoaded
        || this.bandwidthLoaded
        || this.bandwidthPercentileLoaded
        || this.statusCodesLoaded
        || this.statusCodesDetailsLoaded
    },
  },
  created() {
    this.fetchDistributions()
  },
  methods: {
    async changeView(view) {
      try {
        await this.$router.push({ name: view })
      } catch (error) {
      // Handle error if needed
      }
    },
    async applyFilters(data) {
      if (this.distributionsEmpty) {
        this.infoToast('There are no distributions created...')
      } else {
        this.$store.commit('analytics/RESET_TRAFFIC')
        const { filterName } = data
        this.filterVal = filterName
        this.staticAggregation = this.getStaticAggregation(filterName)
        this.timeInterval = this.getTimeInterval(filterName)
        const dataFetchList = [
          'fetchBandwidth', 'fetchBandwidthPercentile',
          'fetchTraffic', 'fetchTrafficSum',
          'fetchHitMisses', 'fetchHitMissesDetails', 'fetchHitMissesSum',
          'fetchStatusCodes', 'fetchStatusCodesDetails',
        ]
        if (dataFetchList.includes('fetchTraffic')) {
          const aggregation = this.staticAggregation
          const queries = [
            {
              distributionIds: data?.selectedIds?.length > 0 ? data?.selectedIds : this.defaultDistributionId,
              aggregation,
              from: this.currentTimestamp - this.timeInterval,
              to: this.currentTimestamp,
              currentTimestamp: true,
            },
            {
              distributionIds: data?.selectedIds?.length > 0 ? data?.selectedIds : this.defaultDistributionId,
              aggregation,
              from: this.currentTimestamp - this.timeInterval * 2,
              to: this.currentTimestamp - this.timeInterval,
            },
            {
              distributionIds: data?.selectedIds?.length > 0 ? data?.selectedIds : this.defaultDistributionId,
              aggregation,
              from: this.currentTimestamp - this.timeInterval * 3,
              to: this.currentTimestamp - this.timeInterval * 2,
            },
          ]
          for (let i = 0; i < 3; i++) {
            try {
            // eslint-disable-next-line no-await-in-loop
              await this.$store.dispatch('analytics/fetchTraffic', queries[i])
            } catch (error) {
              this.infoToast(`No data for traffic at ${new Date(queries[i].from * 1000).toLocaleDateString()} ` || error)
            }
          }
        }

        const fetchDataPromises = dataFetchList
          .filter(actionType => actionType !== 'fetchTraffic')
          .map(actionType => this.fetchData(actionType, data, {}))

        await Promise.all(fetchDataPromises)
      }
    },
    getStaticAggregation(option) {
      const staticAggregation = {
        '4-h': '5-m', // static aggreation for 4-h
        '12-h': '10-m', // static aggreation for 12-h
        '24-h': '30-m', // static aggreation for 24-h
        '48-h': '1-h', //  static aggreation for 48-h
        '72-h': '2-h', // static aggreation for 72-h
        '4-d': '3-h', // static aggreation for 4-d
        '30-d': '1-d', //  static aggreation for 30-d
      }
      return staticAggregation[option]
    },
    getTimeInterval(option) {
      const timeIntervals = {
        '4-h': 4 * 60 * 60, // 4 hours
        '12-h': 12 * 60 * 60, // 12 hours
        '24-h': 24 * 60 * 60, // 24 hours
        '48-h': 2 * 24 * 60 * 60, // 48 hours
        '72-h': 3 * 24 * 60 * 60, // 72 hours
        '4-d': 4 * 24 * 60 * 60, // 4 days
        '30-d': 30 * 24 * 60 * 60, // 30 days
      }
      return timeIntervals[option]
    },
    fetchData(actionType, data, successCallback) {
      this.selectedIds = data?.selectedIds?.length > 0 ? data?.selectedIds : this.defaultDistributionId
      let aggregation

      if (['fetchBandwidth', 'fetchTraffic', 'fetchStatusCodes', 'fetchStatusCodesDetails', 'fetchHitMisses'].includes(actionType)) {
        aggregation = this.staticAggregation
      } else {
        aggregation = data.filterName || '1-d'
      }
      const query = {
        distributionIds: this.selectedIds,
        aggregation,
        from: this.currentTimestamp - this.timeInterval,
        to: this.currentTimestamp - 4,
      }
      try {
        const response = this.$store.dispatch(`analytics/${actionType}`, query)
        if (typeof successCallback === 'function') {
          successCallback(response)
        }
      } catch (error) {
        console.error('ERROR MESSAGE', error)
        const errorMessage = error.response?.data?.errors[0]
      || `Something went wrong while fetching ${actionType.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()}.`
        this.errorToast(errorMessage)
      }
    },
    async fetchDistributions() {
      const distributionQuery = {
        pageIndex: this.currentPage,
        pageSize: this.pageSize,
        state: 2, // 2 for any state
      }

      try {
        const res = await this.$store.dispatch('cdn/fetchDistributions', distributionQuery)
        const { result } = res.data

        if (result.length === 0) {
          this.infoToast('There are no created distributions')
        } else if (result.length !== 0) {
          this.defaultDistributionId.push(result[0].distributionId)
          const dataFetchList = [
            'fetchBandwidth', 'fetchBandwidthPercentile',
            'fetchTraffic', 'fetchTrafficSum',
            'fetchHitMisses', 'fetchHitMissesDetails', 'fetchHitMissesSum',
            'fetchStatusCodes', 'fetchStatusCodesDetails',
          ]

          const fetchDataPromises = dataFetchList
            .filter(actionType => actionType !== 'fetchTraffic')
            .map(actionType => this.fetchData(actionType, {}))

          if (dataFetchList.includes('fetchTraffic')) {
            const aggregation = this.staticAggregation
            const queries = [
              {
                distributionIds: this.defaultDistributionId,
                aggregation,
                from: this.currentTimestamp - this.timeInterval,
                to: this.currentTimestamp,
              },
              {
                distributionIds: this.defaultDistributionId,
                aggregation,
                from: this.currentTimestamp - this.timeInterval * 2,
                to: this.currentTimestamp - this.timeInterval,
              },
              {
                distributionIds: this.defaultDistributionId,
                aggregation,
                from: this.currentTimestamp - this.timeInterval * 3,
                to: this.currentTimestamp - this.timeInterval * 2,
              },
            ]
            for (let i = 0; i < 3; i++) {
              try {
                // eslint-disable-next-line no-await-in-loop
                await this.$store.dispatch('analytics/fetchTraffic', queries[i])
              } catch (error) {
                this.errorToast(`No data for traffic at ${new Date(queries[i].from * 1000).toLocaleDateString()} ` || error)
              }
            }
          }
          await Promise.all(fetchDataPromises)
        } else {
          this.errorToast('There are no distributions.')
        }
      } catch (error) {
        this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while fetching distributions.')
      }
    },
    fetchDistrubutionsForScroll(query) {
      if (query.currentPage < Math.ceil(this.totalCount / this.pageSize) || !query.initialSelectionDone) {
        this.$store
          .dispatch('cdn/fetchScrolledDistributions', {
            pageIndex: this.currentPage,
            pageSize: this.pageSize,
            state: 2,
          })
          .then(() => {
            if (this.distributions.length > 0 && !query.initialSelectionDone) {
              query.selectedOptions.push(this.distributions[0].distributionId)
              query.initialSelectionDone = true
            }
          })
          .catch(error => {
            this.errorToast(
            error.response?.data?.errors[0]
            || 'Something went wrong while fetching distributions.',
            )
          })
      }
    },
  },

}
</script>

<style lang="scss" scoped>
.dashboardWrapper {
  margin: auto;
  max-width: 1920px;
  width: 100%;
  height: 100%;
}
.custom-position::v-deep{
  .popup {
    top: 20%;
  }
}

.gap-16{
  gap:16px
}
</style>
